// Minimal, reusable popup-based OAuth helper for Supabase.

import type { AuthChangeEvent, Provider, Session, SupabaseClient } from "@supabase/supabase-js";

export interface SupabaseSessionOptions {
  /** OAuth provider. Default: 'google'. Example: 'google' | 'github' | 'azure' */
  provider?: Provider;
  /** URL the OAuth flow returns to (page should load Supabase with `detectSessionInUrl:true`). */
  redirectTo?: string;
  /** `window.open` features string. */
  popupFeatures?: string;
  /** Milliseconds before login is considered timed out. Default: 120_000. */
  timeoutMs?: number;
  /** Provider-specific OAuth scopes. E.g. "email profile openid" */
  scopes?: string;
  /** Extra provider query params. E.g. { access_type: "offline", prompt: "consent" } */
  queryParams?: Record<string, string>;
  /** Hook invoked once a session is available. */
  onSignedIn?: (session: Session) => void;
}

/**
 * Popup OAuth and resolve to a Supabase `Session` once signed in.
 *
 * const supabase = createClient("https://PROJECT.supabase.co", "KEY", { auth: {...} });
 * const session = await ensureSupabaseSession(supabase, { provider: "google" });
 *
 * How it works (high level)
 * 1) If a session already exists, return it.
 * 2) Else, ask Supabase for an OAuth URL with `{ skipBrowserRedirect: true }`.
 * 3) Try to open a popup; if blocked (no user gesture), fall back to full-page redirect.
 * 4) On completion, Supabase stores session and **broadcasts** `SIGNED_IN` to same-origin tabs.
 *    We listen for that (preferred), and also for a `postMessage` backup from the popup.
 *
 * Why backup? Broadcast can be delayed by some browser/storage quirks. The popup can call:
 *   `window.opener?.postMessage({ type: 'supabase:SIGNED_IN' }, origin)`
 * We listen for both and resolve the first that arrives.
 */
export async function ensureSupabaseSession(
  client: SupabaseClient,
  {
    provider = "google" as Provider,
    redirectTo = location.origin + location.pathname,
    popupFeatures = "width=480,height=640,menubar=no,toolbar=no",
    timeoutMs = 120_000,
    scopes,
    queryParams,
    onSignedIn,
  }: SupabaseSessionOptions = {},
): Promise<Session> {
  // 1) Short-circuit if already signed in
  {
    const { data: { session } } = await client.auth.getSession();
    if (session) return session;
  }

  // 2) Ask Supabase for an OAuth URL (without auto-redirecting this window)
  const { data: oauthStart, error } = await client.auth.signInWithOAuth({
    provider,
    options: { redirectTo, skipBrowserRedirect: true, scopes, queryParams },
  });
  if (error) throw error;
  const oauthUrl = oauthStart?.url;
  if (!oauthUrl) throw new Error("No OAuth URL returned");

  // 3) Try popup. If blocked, fall back to full-page redirect.
  // Browsers block `window.open` unless triggered by a user gesture (e.g., click).
  const popup = window.open(oauthUrl, "supabase-oauth", popupFeatures);
  if (!popup) {
    location.assign(oauthUrl);
    return new Promise<Session>(() => {/* navigation takes over */});
  }

  // 4) Wait for SIGNED_IN via auth broadcast or postMessage backup
  return new Promise<Session>((resolve, reject) => {
    let settled = false;
    const ac = new AbortController();

    const settle = (fn: () => void) => {
      if (settled) return;
      settled = true;
      cleanup();
      fn();
    };

    const finalize = async () => {
      const { data: { session } } = await client.auth.getSession();
      if (!session) throw new Error("No session after sign-in");
      onSignedIn?.(session);
      return session;
    };

    // Clear listeners/intervals/timeouts and close popup (prevents leaks/double-resolve)
    const cleanup = () => {
      ac.abort(); // removes event listeners registered with { signal }
      try {
        authSub?.data?.subscription?.unsubscribe?.();
      } catch { /* noop */ }
      clearInterval(pollClose);
      clearTimeout(timer);
      try {
        popup.close();
      } catch { /* noop */ }
    };

    // Preferred path: Supabase cross-tab auth broadcast
    const authSub = client.auth.onAuthStateChange((evt: AuthChangeEvent) => {
      if (evt === "SIGNED_IN") {
        finalize().then((s) => settle(() => resolve(s))).catch((err) => settle(() => reject(err)));
      }
    });

    // Backup: postMessage from popup (call this in the popup after session is set)
    const onMessage = (ev: MessageEvent<{ type?: string; }>) => {
      if (ev.data?.type === "supabase:SIGNED_IN") {
        finalize().then((s) => settle(() => resolve(s))).catch((err) => settle(() => reject(err)));
      }
    };
    window.addEventListener("message", onMessage, { signal: ac.signal });

    // Detect user closing the popup without logging in
    const pollClose = window.setInterval(() => {
      if (!popup || popup.closed) settle(() => reject(new Error("Login popup closed")));
    }, 300);

    // Defensive timeout
    const timer = window.setTimeout(() => {
      settle(() => reject(new Error("Login timed out")));
    }, timeoutMs);
  });
}

export default ensureSupabaseSession;
